import { useState } from 'react';
import { useInView } from '../hooks/useInView';
import MailtoForm from '../components/MailtoForm';
import fleetPhoto from '../assets/Real Operational Experience.jpg';
import './LaunchSection.css';

export default function LaunchSection() {
  const [ref, inView] = useInView();
  const [formOpen, setFormOpen] = useState(false);

  return (
    <section className="launch" id="launch" aria-labelledby="launch-heading">
      <div className="launch__top-bar" aria-hidden="true" />
      <div className="container" ref={ref}>
        {/* Section label */}
        <p className={`section-label fade-up ${inView ? 'in-view' : ''}`}>
          Product Focus
        </p>

        {/* Main heading */}
        <h2
          id="launch-heading"
          className={`launch__heading fade-up d1 ${inView ? 'in-view' : ''}`}
        >
          Built Around the<br />
          <span className="gradient-text">Full Taxi Operations Lifecycle</span>
        </h2>

        <p className={`launch__intro fade-up d2 ${inView ? 'in-view' : ''}`}>
          TaxiOps brings dispatch, driver execution, live location, fare records,
          compliance workflows, reporting, and intelligence into one connected platform.
          The product is shaped around the pressure points operators face every day:
          knowing where drivers are, keeping trips moving, protecting records, and
          turning operational data into action.
        </p>

        {/* Stats */}
        <div className={`launch__stats fade-up d3 ${inView ? 'in-view' : ''}`}>
          <div className="launch__stat-card">
            <div className="launch__stat-icon launch__stat-icon--yellow" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2a4 4 0 1 1 0 8 4 4 0 0 1 0-8zM3 18a7 7 0 0 1 14 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="launch__stat-value">One</span>
            <span className="launch__stat-name">Connected Backend</span>
            <span className="launch__stat-desc">Dispatch, driver app, and EnrollMe records aligned</span>
          </div>

          <div className="launch__stat-card">
            <div className="launch__stat-icon launch__stat-icon--blue" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2l1.8 4.2L16 7.6l-3 2.9.7 4.1L10 12.4l-3.7 2.2.7-4.1-3-2.9 4.2-.6L10 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="launch__stat-value">Real Time</span>
            <span className="launch__stat-name">Live Operations</span>
            <span className="launch__stat-desc">Driver presence, trip state, and dispatch map updates</span>
          </div>

          <div className="launch__stat-card">
            <div className="launch__stat-icon launch__stat-icon--green" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 6v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="launch__stat-value">Offline</span>
            <span className="launch__stat-name">Trip Reliability</span>
            <span className="launch__stat-desc">Queued mobile trip events sync when service returns</span>
          </div>
        </div>

        {/* Capacity reached card */}
        <div className={`launch__capacity fade-up d4 ${inView ? 'in-view' : ''}`}>
          <div className="launch__capacity-header">
            <div className="launch__capacity-badge">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M7 1.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11zM7 5v3M7 9.5h.01" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
              Availability Status
            </div>
            <h3 className="launch__capacity-title">Focused Implementation Period</h3>
            <p className="launch__capacity-status">
              TaxiOps is not accepting open self-service enrollments at this time.
            </p>
          </div>
          <div className="launch__capacity-divider" aria-hidden="true" />
          <div className="launch__capacity-body">
            <p className="launch__capacity-message">
              The current focus is product reliability, field feedback, operational
              intelligence, and careful deployment. Organizations interested in future
              availability can register interest with Old Alex Hub.
            </p>
            <button
              type="button"
              className="btn btn--outline-yellow"
              onClick={() => setFormOpen(true)}
            >
              Register Future Interest
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                <path d="M2.5 7.5h10M9 4l3.5 3.5L9 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Placeholder for fleet operations photo */}
        <div className={`launch__image-row fade-up d5 ${inView ? 'in-view' : ''}`}>
          <div className="launch__image-wrap">
            <img
              src={fleetPhoto}
              alt="Taxi and NEMT Fleet Operations"
              className="launch__fleet-img"
            />
          </div>
          <div className="launch__image-content">
            <h3 className="launch__image-title">Real Operational Experience</h3>
            <p className="launch__image-text">
              TaxiOps was built from inside the transportation business — not as a
              technology concept, but as a direct response to the operational realities
              facing Taxi and NEMT providers every day.
            </p>
            <p className="launch__image-text">
              Dispatch pressure, driver accountability, vehicle compliance, fare
              accuracy, trip execution, and operational reporting are not abstractions.
              They are the foundation TaxiOps was built upon.
            </p>
            <div className="launch__pillars">
              {[
                { label: 'Live Operations', icon: '⚡' },
                { label: 'Real Compliance', icon: '✓' },
                { label: 'Driver-First', icon: '◈' },
                { label: 'Dispatch Proven', icon: '◎' },
              ].map(({ label, icon }) => (
                <span key={label} className="launch__pillar">
                  <span aria-hidden="true">{icon}</span> {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <MailtoForm open={formOpen} onClose={() => setFormOpen(false)} />
    </section>
  );
}
